'use client';
import React, { useState, useMemo } from 'react';
import { Bell, Plus, Search, Filter, Trash2, Mail, Smartphone, MonitorSmartphone, Edit3, ChevronDown, AlertTriangle, Ship, Package,  } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import AddAlertModal from './AddAlertModal';
import { toast } from 'sonner';

interface Alert {
  id: string;
  blNumber: string;
  carrier: string;
  vesselName: string;
  currentStatus: string;
  origin: string;
  destination: string;
  eta: string;
  alertDate: string;
  channels: string[];
  lastTriggered: string | null;
  createdAt: string;
  active: boolean;
}

// Mock data — backend integration point: replace with GET /api/alerts?userId=xxx
const MOCK_ALERTS: Alert[] = [
  {
    id: 'alert-001',
    blNumber: 'MAEU123456789',
    carrier: 'Maersk',
    vesselName: 'Maersk Emerald',
    currentStatus: 'In Transit',
    origin: 'JNPT (Nhava Sheva)',
    destination: 'Rotterdam',
    eta: '18/09/2026',
    alertDate: '17/09/2026',
    channels: ['Email', 'SMS'],
    lastTriggered: null,
    createdAt: '01/09/2026',
    active: true,
  },
  {
    id: 'alert-002',
    blNumber: 'MSCU987654321',
    carrier: 'MSC',
    vesselName: 'MSC Ambra',
    currentStatus: 'Customs Hold',
    origin: 'Mundra Port',
    destination: 'Felixstowe',
    eta: '22/09/2026',
    alertDate: '21/09/2026',
    channels: ['Email', 'Push'],
    lastTriggered: '05/09/2026',
    createdAt: '28/08/2026',
    active: true,
  },
  {
    id: 'alert-003',
    blNumber: 'HLCU456789012',
    carrier: 'Hapag-Lloyd',
    vesselName: 'Hamburg Express',
    currentStatus: 'Arrived at POD',
    origin: 'Chennai Port',
    destination: 'Hamburg',
    eta: '04/09/2026',
    alertDate: '03/09/2026',
    channels: ['Email'],
    lastTriggered: '03/09/2026',
    createdAt: '20/08/2026',
    active: false,
  },
  {
    id: 'alert-004',
    blNumber: 'CMAU789012345',
    carrier: 'CMA CGM',
    vesselName: 'CMA CGM Thalassa',
    currentStatus: 'Vessel Departed',
    origin: 'Kolkata Port',
    destination: 'Le Havre',
    eta: '28/09/2026',
    alertDate: '27/09/2026',
    channels: ['SMS'],
    lastTriggered: null,
    createdAt: '03/09/2026',
    active: true,
  },
  {
    id: 'alert-005',
    blNumber: 'ONEY345678901',
    carrier: 'ONE Line',
    vesselName: 'ONE Commitment',
    currentStatus: 'Discharged',
    origin: 'Cochin Port',
    destination: 'Colombo',
    eta: '06/09/2026',
    alertDate: '05/09/2026',
    channels: ['Email', 'SMS', 'Push'],
    lastTriggered: '05/09/2026',
    createdAt: '25/08/2026',
    active: false,
  },
  {
    id: 'alert-006',
    blNumber: 'EGLV234567890',
    carrier: 'Evergreen',
    vesselName: 'Ever Given',
    currentStatus: 'Booking Confirmed',
    origin: 'JNPT (Nhava Sheva)',
    destination: 'Singapore',
    eta: '14/09/2026',
    alertDate: '13/09/2026',
    channels: ['Email'],
    lastTriggered: null,
    createdAt: '05/09/2026',
    active: true,
  },
  {
    id: 'alert-007',
    blNumber: 'SCIN112233445',
    carrier: 'SCI India',
    vesselName: 'SCI Yamuna',
    currentStatus: 'In Transit',
    origin: 'Visakhapatnam',
    destination: 'Kandla Port',
    eta: '10/09/2026',
    alertDate: '09/09/2026',
    channels: ['SMS'],
    lastTriggered: null,
    createdAt: '04/09/2026',
    active: true,
  },
  {
    id: 'alert-008',
    blNumber: 'ZIMU556677889',
    carrier: 'ZIM',
    vesselName: 'ZIM Haifa',
    currentStatus: 'Delayed',
    origin: 'Mundra Port',
    destination: 'Haifa',
    eta: '30/09/2026',
    alertDate: '29/09/2026',
    channels: ['Email', 'SMS'],
    lastTriggered: '04/09/2026',
    createdAt: '01/09/2026',
    active: true,
  },
  {
    id: 'alert-009',
    blNumber: 'COSU998877665',
    carrier: 'COSCO',
    vesselName: 'COSCO Shipping Aries',
    currentStatus: 'Cargo Received',
    origin: 'Chennai Port',
    destination: 'Shanghai',
    eta: '20/09/2026',
    alertDate: '19/09/2026',
    channels: ['Push'],
    lastTriggered: null,
    createdAt: '06/09/2026',
    active: true,
  },
  {
    id: 'alert-010',
    blNumber: 'HLCU321654987',
    carrier: 'Hapag-Lloyd',
    vesselName: 'Antwerp Express',
    currentStatus: 'Delivery Order Issued',
    origin: 'JNPT (Nhava Sheva)',
    destination: 'Antwerp',
    eta: '02/09/2026',
    alertDate: '01/09/2026',
    channels: ['Email'],
    lastTriggered: '01/09/2026',
    createdAt: '15/08/2026',
    active: false,
  },
];

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  Email: <Mail size={11} />,
  SMS: <Smartphone size={11} />,
  Push: <MonitorSmartphone size={11} />,
};

const CHANNEL_COLORS: Record<string, string> = {
  Email: 'bg-info-bg text-info border border-blue-200',
  SMS: 'bg-success-bg text-success border border-green-200',
  Push: 'bg-warning-bg text-warning border border-yellow-200',
};

type SortField = 'blNumber' | 'carrier' | 'alertDate' | 'eta' | 'createdAt';

export default function MyAlertsScreen() {
  const [alerts, setAlerts] = useState<Alert[]>(MOCK_ALERTS);
  const [search, setSearch] = useState('');
  const [filterCarrier, setFilterCarrier] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterChannel, setFilterChannel] = useState('All');
  const [filterActive, setFilterActive] = useState<'All' | 'Active' | 'Inactive'>('All');
  const [selected, setSelected] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [deleteTarget, setDeleteTarget] = useState<string[] | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const carriers = useMemo(() => ['All', ...Array.from(new Set(MOCK_ALERTS.map((a) => a.carrier)))], []);
  const statuses = useMemo(() => ['All', ...Array.from(new Set(MOCK_ALERTS.map((a) => a.currentStatus)))], []);

  const filtered = useMemo(() => {
    let list = alerts.filter((a) => {
      const q = search.toLowerCase();
      if (q && !a.blNumber.toLowerCase().includes(q) && !a.carrier.toLowerCase().includes(q) && !a.destination.toLowerCase().includes(q) && !a.vesselName.toLowerCase().includes(q)) return false;
      if (filterCarrier !== 'All' && a.carrier !== filterCarrier) return false;
      if (filterStatus !== 'All' && a.currentStatus !== filterStatus) return false;
      if (filterChannel !== 'All' && !a.channels.includes(filterChannel)) return false;
      if (filterActive === 'Active' && !a.active) return false;
      if (filterActive === 'Inactive' && a.active) return false;
      return true;
    });

    list = [...list].sort((a, b) => {
      const valA = a[sortField] ?? '';
      const valB = b[sortField] ?? '';
      const cmp = String(valA).localeCompare(String(valB));
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [alerts, search, filterCarrier, filterStatus, filterChannel, filterActive, sortField, sortDir]);

  const allSelected = filtered.length > 0 && filtered.every((a) => selected.includes(a.id));

  const toggleSelect = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelected((prev) => prev.filter((id) => !filtered.map((a) => a.id).includes(id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...filtered.map((a) => a.id)])));
    }
  };

  const confirmDelete = (ids: string[]) => setDeleteTarget(ids);

  const executeDelete = () => {
    if (!deleteTarget) return;
    setAlerts((prev) => prev.filter((a) => !deleteTarget.includes(a.id)));
    setSelected((prev) => prev.filter((id) => !deleteTarget.includes(id)));
    toast.success(`${deleteTarget.length} alert${deleteTarget.length > 1 ? 's' : ''} deleted.`);
    setDeleteTarget(null);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleActive = (id: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, active: !a.active } : a))
    );
  };

  const SortIcon = ({ field }: { field: SortField }) => (
    <ChevronDown
      size={12}
      className={`ml-1 inline transition-transform ${
        sortField === field ? (sortDir === 'asc' ? 'rotate-180 text-primary' : 'text-primary') : 'text-muted-foreground'
      }`}
    />
  );

  const activeCount = alerts.filter((a) => a.active).length;
  const delayedCount = alerts.filter((a) => a.currentStatus === 'Delayed' || a.currentStatus === 'Customs Hold').length;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Alerts</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {activeCount} active · {alerts.length} total subscriptions
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="btn-primary flex items-center gap-2 px-4 py-2.5 text-sm shrink-0"
        >
          <Plus size={16} />
          Add New Alert
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { id: 'sum-active', label: 'Active Alerts', value: activeCount, color: 'text-success', bg: 'bg-success/10' },
          { id: 'sum-attention', label: 'Need Attention', value: delayedCount, color: 'text-warning', bg: 'bg-warning/10' },
          { id: 'sum-total', label: 'Total Tracked', value: alerts.length, color: 'text-primary', bg: 'bg-primary/10' },
          { id: 'sum-triggered', label: 'Triggered Today', value: alerts.filter((a) => a.lastTriggered === '06/09/2026').length, color: 'text-accent', bg: 'bg-accent/10' },
        ].map((s) => (
          <div key={s.id} className="card-surface p-4 shadow-card flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center`}>
              <Bell size={18} className={s.color} />
            </div>
            <div>
              <p className={`text-2xl font-bold font-tabular ${s.color}`}>{s.value}</p>
              <p className="text-xs font-medium text-muted-foreground">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Delayed/customs warning banner */}
      {delayedCount > 0 && (
        <div className="bg-warning-bg border border-yellow-300 rounded-xl px-4 py-3 flex items-center gap-3">
          <AlertTriangle size={18} className="text-warning shrink-0" />
          <p className="text-sm font-medium text-warning">
            <span className="font-bold">{delayedCount} shipment{delayedCount > 1 ? 's' : ''}</span> require{delayedCount === 1 ? 's' : ''} attention — Delayed or Customs Hold status detected.
          </p>
        </div>
      )}

      {/* Filters and search */}
      <div className="card-surface p-4 shadow-card">
        <div className="flex flex-col gap-3">
          {/* Search */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search by BL number, carrier, vessel, or destination..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field w-full h-10 pl-9 pr-4 text-sm"
            />
          </div>

          {/* Filter row */}
          <div className="flex flex-wrap gap-2 items-center">
            <Filter size={14} className="text-muted-foreground shrink-0" />

            {/* Carrier */}
            <div className="relative">
              <select
                value={filterCarrier}
                onChange={(e) => setFilterCarrier(e.target.value)}
                className="input-field h-8 pl-3 pr-7 text-xs font-medium appearance-none cursor-pointer"
              >
                {carriers.map((c) => (
                  <option key={`filt-carrier-${c}`} value={c}>{c === 'All' ? 'All Carriers' : c}</option>
                ))}
              </select>
              <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>

            {/* Status */}
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="input-field h-8 pl-3 pr-7 text-xs font-medium appearance-none cursor-pointer"
              >
                {statuses.map((s) => (
                  <option key={`filt-status-${s}`} value={s}>{s === 'All' ? 'All Statuses' : s}</option>
                ))}
              </select>
              <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>

            {/* Channel */}
            <div className="relative">
              <select
                value={filterChannel}
                onChange={(e) => setFilterChannel(e.target.value)}
                className="input-field h-8 pl-3 pr-7 text-xs font-medium appearance-none cursor-pointer"
              >
                {['All', 'Email', 'SMS', 'Push'].map((ch) => (
                  <option key={`filt-ch-${ch}`} value={ch}>{ch === 'All' ? 'All Channels' : ch}</option>
                ))}
              </select>
              <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>

            {/* Active */}
            <div className="relative">
              <select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value as 'All' | 'Active' | 'Inactive')}
                className="input-field h-8 pl-3 pr-7 text-xs font-medium appearance-none cursor-pointer"
              >
                {['All', 'Active', 'Inactive'].map((v) => (
                  <option key={`filt-active-${v}`} value={v}>{v === 'All' ? 'All States' : v}</option>
                ))}
              </select>
              <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>

            {(search || filterCarrier !== 'All' || filterStatus !== 'All' || filterChannel !== 'All' || filterActive !== 'All') && (
              <button
                onClick={() => {
                  setSearch('');
                  setFilterCarrier('All');
                  setFilterStatus('All');
                  setFilterChannel('All');
                  setFilterActive('All');
                }}
                className="text-xs font-semibold text-danger hover:text-danger/80 transition-colors"
              >
                Clear filters
              </button>
            )}

            <span className="ml-auto text-xs text-muted-foreground">
              {filtered.length} of {alerts.length} alerts
            </span>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="slide-up bg-primary text-primary-foreground rounded-xl px-5 py-3 flex items-center gap-4 shadow-elevated">
          <span className="text-sm font-semibold">{selected.length} selected</span>
          <div className="flex-1" />
          <button
            onClick={() => confirmDelete(selected)}
            className="flex items-center gap-2 text-sm font-semibold bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Trash2 size={14} />
            Delete Selected
          </button>
          <button
            onClick={() => setSelected([])}
            className="text-sm text-white/70 hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card-surface shadow-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
              <Bell size={24} className="text-muted-foreground" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-1">No alerts found</h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              {search || filterCarrier !== 'All' || filterStatus !== 'All' ?'No alerts match your current filters. Try adjusting the search or filters above.' :'You haven\'t set up any BL tracking alerts yet. Click "Add New Alert" to get started.'}
            </p>
            {!search && filterCarrier === 'All' && (
              <button
                onClick={() => setAddOpen(true)}
                className="btn-primary px-5 py-2.5 text-sm mt-4 flex items-center gap-2"
              >
                <Plus size={15} />
                Add Your First Alert
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted border-b border-border">
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="w-4 h-4 accent-primary rounded cursor-pointer"
                    />
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('blNumber')}
                  >
                    BL Number <SortIcon field="blNumber" />
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('carrier')}
                  >
                    Carrier <SortIcon field="carrier" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                    Vessel Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">
                    Route
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground hidden md:table-cell"
                    onClick={() => handleSort('eta')}
                  >
                    ETA <SortIcon field="eta" />
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground hidden md:table-cell"
                    onClick={() => handleSort('alertDate')}
                  >
                    Alert Date <SortIcon field="alertDate" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">
                    Channels
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden xl:table-cell">
                    Last Triggered
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    State
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((alert, idx) => (
                  <tr
                    key={alert.id}
                    className={`border-b border-border transition-colors hover:bg-muted/50 ${
                      idx % 2 === 0 ? 'bg-card' : 'bg-background/50'
                    } ${selected.includes(alert.id) ? 'bg-secondary/50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(alert.id)}
                        onChange={() => toggleSelect(alert.id)}
                        className="w-4 h-4 accent-primary rounded cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Package size={13} className="text-muted-foreground shrink-0" />
                        <span className="font-semibold text-foreground font-tabular text-xs">
                          {alert.blNumber}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Ship size={13} className="text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium text-foreground">{alert.carrier}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex items-center gap-1.5">
                        <Ship size={13} className="text-accent shrink-0" />
                        <span className="text-xs font-semibold text-foreground">{alert.vesselName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={alert.currentStatus} size="xs" />
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{alert.origin}</span>
                        <span className="mx-1">→</span>
                        <span>{alert.destination}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm font-medium text-foreground font-tabular">{alert.eta}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm font-medium text-foreground font-tabular">{alert.alertDate}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="flex items-center gap-1 flex-wrap">
                        {alert.channels.map((ch) => (
                          <span
                            key={`${alert.id}-ch-${ch}`}
                            className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full ${CHANNEL_COLORS[ch]}`}
                          >
                            {CHANNEL_ICONS[ch]}
                            {ch}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <span className="text-xs text-muted-foreground">
                        {alert.lastTriggered ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleActive(alert.id)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                          alert.active ? 'bg-success' : 'bg-muted-foreground/30'
                        }`}
                        title={alert.active ? 'Pause alert' : 'Activate alert'}
                        role="switch"
                        aria-checked={alert.active}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                            alert.active ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-primary transition-colors"
                          title="Edit alert date and channels"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => confirmDelete([alert.id])}
                          className="p-1.5 rounded-lg text-muted-foreground hover:bg-danger/10 hover:text-danger transition-colors"
                          title="Delete this alert — cannot be undone"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
            <span>
              Showing <span className="font-semibold text-foreground">{filtered.length}</span> of{' '}
              <span className="font-semibold text-foreground">{alerts.length}</span> alerts
            </span>
            <div className="flex items-center gap-1">
              {[1].map((p) => (
                <button
                  key={`page-${p}`}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-semibold bg-primary text-primary-foreground"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete confirm modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Confirm Delete"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-danger/10 flex items-center justify-center shrink-0">
              <Trash2 size={18} className="text-danger" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                Delete {deleteTarget?.length === 1 ? 'this alert' : `${deleteTarget?.length} alerts`}?
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                You will no longer receive notifications for the selected BL{deleteTarget?.length === 1 ? '' : 's'}. This action cannot be undone.
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setDeleteTarget(null)}
              className="px-4 py-2 text-sm font-semibold text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={executeDelete}
              className="px-4 py-2 text-sm font-semibold text-white bg-danger rounded-lg hover:bg-danger/90 transition-colors"
            >
              Delete Alert{deleteTarget?.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add alert modal */}
      <AddAlertModal open={addOpen} onClose={() => setAddOpen(false)} onAdded={(a) => {
        setAlerts((prev) => [a, ...prev]);
        toast.success(`Alert added for BL ${a.blNumber}`);
      }} />
    </div>
  );
}